import type { ReactNode } from "react";
import { AlignLeft, BookA, FlaskConical, Trash2 } from "lucide-react";
import type {
  ClaimSubsection,
  DefinitionLine,
  DefinitionsSubsection,
  ExamplesSubsection,
  FigureSubsection,
  LadderNow,
  TextSubsection,
} from "@patent-studio/api-types";
import { OriginPill } from "@/components/patterns/OriginPill";
import { useSortableItem } from "@/components/patterns/SortableList";
import { IconButton } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { Thumbnail } from "@/components/ui/Thumbnail";
import { cn } from "@/lib/utils";
import { ChildLines, type ChildLine } from "./ChildLines";
import { type LadderActions, RangeLadders } from "./RangeLadders";
import type { DefinitionWords } from "./framework-edits";
import { InlineLine } from "./InlineLine";
import { TEXT_LIMITS } from "@/lib/text-limits";

export type Subsection =
  | TextSubsection
  | FigureSubsection
  | ClaimSubsection
  | ExamplesSubsection
  | DefinitionsSubsection;

export interface Labels {
  figure: (fileId: string) => string | undefined;
  claim: (claimId: string) => number | undefined;
  thumbnail: (fileId: string) => string | undefined;
  /** The figure's view, as its Brief Figure Description in Figures says it. */
  view: (fileId: string) => string | undefined;
  /** The claim's title, its preamble, as Claim Strategy holds it. */
  claimTitle: (claimId: string) => string | undefined;
  /** The independent claim's category, as Claim Strategy names it now. */
  category: (claimId: string) => string | undefined;
  /** The claims beneath the independent claim, which its row brings to the writer: "with claims 2 to 7". */
  beneath: (claimId: string) => string | undefined;
  /** The term of an element or rung of the invention, which a claimed range recites. */
  recital: (recitalId: string) => string | undefined;
  /** What the read says of each ladder on one claim row, as the claims now stand. */
  rangesNow: (subsectionId: string) => LadderNow[];
}

const LEAD_ICONS: Record<Exclude<Subsection["kind"], "figure">, ReactNode> = {
  text: <AlignLeft />,
  claim: <AlignLeft />,
  examples: <FlaskConical />,
  definitions: <BookA />,
};

function Lead({
  subsection,
  labels,
}: {
  subsection: Subsection;
  labels: Labels;
}) {
  if (subsection.kind !== "figure") {
    return (
      <Thumbnail src={undefined} slim>
        {LEAD_ICONS[subsection.kind]}
      </Thumbnail>
    );
  }
  return <FigureLead fileId={subsection.file_id} labels={labels} />;
}

/** A figure's lead wherever the plan lists one: its drawing, then "FIG. 2A" in violet mono. */
export function FigureLead({
  fileId,
  labels,
}: {
  fileId: string;
  labels: Labels;
}) {
  const label = labels.figure(fileId);
  return (
    <span className="flex flex-none items-center gap-2">
      <Thumbnail src={labels.thumbnail(fileId)} />
      <span className="font-mono text-xs text-primary">
        {label === undefined ? "FIG. ?" : `FIG. ${label}`}
      </span>
    </span>
  );
}

function Words({
  subsection,
  labels,
  readOnly,
  onReword,
}: {
  subsection: Subsection;
  labels: Labels;
  readOnly: boolean;
  onReword: (text: string, putBack: () => void) => void;
}) {
  switch (subsection.kind) {
    case "text":
      return (
        <InlineLine
          value={subsection.text}
          label="Subsection text"
          readOnly={readOnly}
          maxLength={TEXT_LIMITS.subsectionLine}
          onSave={onReword}
          className="text-sm"
        />
      );
    case "figure": {
      const view = labels.view(subsection.file_id);
      return (
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          {view}
        </span>
      );
    }
    case "claim":
      return (
        <ClaimWords
          claimId={subsection.claim_id}
          text={subsection.text}
          labels={labels}
        />
      );
    case "examples":
      return (
        <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          <span className="font-semibold">Examples</span>
          <span className="truncate text-muted-foreground">
            {EXAMPLES_WORDS}
          </span>
        </span>
      );
    case "definitions":
      return (
        <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          <span className="font-semibold">Definitions</span>
          <span className="truncate text-muted-foreground">
            {termsCounted(subsection)}
          </span>
        </span>
      );
  }
}

/** A claim row: its number, the dependent claims it brings, its words and its category. */
function ClaimWords({
  claimId,
  text,
  labels,
}: {
  claimId: string;
  text: string;
  labels: Labels;
}) {
  const number = labels.claim(claimId);
  const category = labels.category(claimId);
  const beneath = labels.beneath(claimId);
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
      <span className="font-semibold">
        {number === undefined ? "Claim" : `Claim ${String(number)}`}
      </span>
      {beneath !== undefined && (
        <span className="shrink-0 text-muted-foreground">{beneath}</span>
      )}
      {text !== "" && (
        <span className="truncate text-muted-foreground">{text}</span>
      )}
      {category !== undefined && (
        <StatusPill
          tone="neutral"
          size="sm"
          plain
          title="Claim category from Claim Strategy"
        >
          {category}
        </StatusPill>
      )}
    </span>
  );
}

/** How many terms the row's lines define, since the lines themselves are drawn beneath it. */
function termsCounted(subsection: DefinitionsSubsection): string {
  const count = (subsection.lines ?? []).length;
  if (count === 0) return "No term defined yet";
  return count === 1 ? "1 term" : `${String(count)} terms`;
}

/** What the Definitions subsection holds, as the plan reads it: the terms it defines. */
export function definitionsWords(subsection: DefinitionsSubsection): string {
  const terms = (subsection.lines ?? []).map((line) => line.key);
  return terms.length === 0 ? "No term defined yet" : terms.join(", ");
}

/** What the Examples subsection stands for, as the plan reads it. */
export const EXAMPLES_WORDS =
  "The disclosure's worked examples, stated as given";

/** How a reader hears a subsection: its figure, its claim or its words. */
export function spoken(subsection: Subsection, labels: Labels): string {
  switch (subsection.kind) {
    case "figure":
      return `FIG. ${labels.figure(subsection.file_id) ?? "?"}`;
    case "claim":
      return `Claim ${String(labels.claim(subsection.claim_id) ?? "?")}`;
    case "text":
      return subsection.text;
    case "examples":
      return "Examples";
    case "definitions":
      return "Definitions";
  }
}

/** What a Definitions row's lines send, one line at a time. */
export interface DefinitionActions {
  add: (words: DefinitionWords, putBack: () => void) => void;
  reword: (itemId: string, words: DefinitionWords, putBack: () => void) => void;
  drop: (itemId: string) => void;
}

interface RowProps {
  subsection: Subsection;
  labels: Labels;
  orphan: boolean;
  readOnly: boolean;
  onReword: (text: string, putBack: () => void) => void;
  onDelete: () => void;
  definitions?: DefinitionActions;
  ladders?: LadderActions;
}

/** A definition as its row draws it; a suggestion cites every source it rests on. */
function definitionLine(line: DefinitionLine): ChildLine {
  const sources = line.sources ?? [];
  return {
    item_id: line.item_id,
    key: line.key,
    text: line.text,
    origin: line.origin,
    source:
      sources.length === 0
        ? undefined
        : {
            // Two passages on one page share a label; the row names the page once.
            label: [...new Set(sources.map((source) => source.label))].join(
              " · ",
            ),
          },
  };
}

/** The lines under a Definitions row: each term and its words, edited where they are drawn. */
function DefinitionLines({
  subsection,
  readOnly,
  actions,
}: {
  subsection: DefinitionsSubsection;
  readOnly: boolean;
  actions?: DefinitionActions;
}) {
  return (
    <div className="pl-11">
      <ChildLines
        items={(subsection.lines ?? []).map(definitionLine)}
        label="Definitions"
        addLabel="Add definition"
        placeholders={{
          key: "Term",
          text: "What it means in this description",
        }}
        readOnly={readOnly || actions === undefined}
        onAdd={({ key, text }, putBack) => {
          if (text !== undefined) actions?.add({ key, text }, putBack);
        }}
        onReword={(itemId, { key, text }, putBack) => {
          if (text !== undefined)
            actions?.reword(itemId, { key, text }, putBack);
        }}
        onDrop={(itemId) => {
          actions?.drop(itemId);
        }}
      />
    </div>
  );
}

/** What a row holds beneath its own line: a Definitions row's lines, or a claim row's range ladders. */
function ChildRows({
  subsection,
  labels,
  readOnly,
  definitions,
  ladders,
}: Pick<
  RowProps,
  "subsection" | "labels" | "readOnly" | "definitions" | "ladders"
>) {
  if (subsection.kind === "definitions")
    return (
      <DefinitionLines
        subsection={subsection}
        readOnly={readOnly}
        actions={definitions}
      />
    );
  if (subsection.kind === "claim" && (subsection.ranges ?? []).length > 0)
    return (
      <RangeLadders
        ladders={subsection.ranges ?? []}
        now={labels.rangesNow(subsection.subsection_id)}
        names={labels}
        readOnly={readOnly}
        actions={ladders}
      />
    );
  return null;
}

function Row({
  subsection,
  labels,
  orphan,
  readOnly,
  onReword,
  onDelete,
  definitions,
  ladders,
  drag,
}: RowProps & {
  drag?: {
    ref: (element: Element | null) => void;
    handle: ReactNode;
    chrome: string;
  };
}) {
  return (
    <Panel
      as="li"
      ref={drag?.ref}
      shape="row"
      pad="line"
      className={cn("space-y-2", drag?.chrome)}
    >
      <div className="flex items-center gap-3">
        {drag?.handle}
        <span className={cn("contents", orphan && "[&>*]:opacity-50")}>
          <Lead subsection={subsection} labels={labels} />
        </span>
        <span
          className={cn(
            "flex min-w-0 flex-1 items-center",
            orphan && "line-through",
          )}
        >
          <Words
            subsection={subsection}
            labels={labels}
            readOnly={readOnly || orphan}
            onReword={onReword}
          />
        </span>
        {orphan ? (
          <StatusPill tone="destructive" size="sm">
            Deleted upstream
          </StatusPill>
        ) : (
          <OriginPill origin={subsection.origin} surface="framework" />
        )}
        {!readOnly && (
          <IconButton
            variant="secondary"
            size="dense"
            aria-label={`Delete subsection "${spoken(subsection, labels)}"`}
            data-subsection-delete=""
            onClick={onDelete}
          >
            <Trash2 />
          </IconButton>
        )}
      </div>
      <ChildRows
        subsection={subsection}
        labels={labels}
        readOnly={readOnly || orphan}
        definitions={definitions}
        ladders={ladders}
      />
    </Panel>
  );
}

function DraggableRow({
  lane,
  index,
  ...row
}: RowProps & { lane: string; index: number }) {
  const drag = useSortableItem({
    id: row.subsection.subsection_id,
    index,
    lane,
    type: "subsection",
    label: `Reorder ${spoken(row.subsection, row.labels)}`,
    size: "sm",
  });
  return <Row {...row} drag={drag} />;
}

/**
 * One subsection of a section: what it is (a figure's thumbnail and label, or a line), its
 * words, who wrote it, and the act that deletes it. It is moved by its handle within and
 * across sections. A figure or claim deleted upstream stays, greyed and struck through and
 * not movable, so its place is seen until the attorney deletes it.
 */
export function SubsectionRow({
  lane,
  index,
  ...row
}: RowProps & { lane: string; index: number }) {
  if (row.readOnly || row.orphan) return <Row {...row} />;
  return <DraggableRow lane={lane} index={index} {...row} />;
}
